import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum ConnectionType {
  SLOW = 'slow',
  MEDIUM = 'medium',
  FAST = 'fast'
}

// Définition d'une interface pour l'API Network Information
interface NetworkInformation {
  effectiveType?: string;
  saveData?: boolean;
  addEventListener?: (type: string, listener: EventListener) => void;
  removeEventListener?: (type: string, listener: EventListener) => void;
}

// Extension du type Navigator pour inclure l'API Network Information
declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ConnectionService {
  private connectionTypeSubject = new BehaviorSubject<ConnectionType>(ConnectionType.FAST);
  private connection: any;

  constructor() {
    // Initialisation de la détection de la connexion
    this.initConnectionMonitoring();
  }

  /**
   * Initialise la surveillance de la connexion réseau
   * Cette méthode est appelée automatiquement dans le constructeur
   * mais peut aussi être appelée manuellement si nécessaire
   */
  public initConnectionMonitoring(): void {
    // Récupération de l'objet de connexion (si disponible)
    this.connection = this.getNetworkConnection();

    if (this.connection) {
      // Détection initiale
      this.detectConnectionSpeed();

      // Surveillance des changements de connexion
      this.connection.addEventListener('change', () => {
        this.detectConnectionSpeed();
      });
    } else {
      // Si l'API n'est pas disponible, on estime la vitesse
      this.estimateConnectionSpeed();
    }
  }

  private getNetworkConnection(): any {
    return (
      navigator.connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection
    );
  }

  private detectConnectionSpeed(): void {
    if (!this.connection) {
      return;
    }

    // Détection basée sur effectiveType
    if (this.connection.effectiveType) {
      switch (this.connection.effectiveType) {
        case 'slow-2g':
        case '2g':
          this.connectionTypeSubject.next(ConnectionType.SLOW);
          break;
        case '3g':
          this.connectionTypeSubject.next(ConnectionType.MEDIUM);
          break;
        case '4g':
        case '5g':
          this.connectionTypeSubject.next(ConnectionType.FAST);
          break;
        default:
          this.connectionTypeSubject.next(ConnectionType.FAST);
      }
    }

    // Si le mode "économie de données" est activé, on considère une connexion lente
    if (this.connection.saveData) {
      this.connectionTypeSubject.next(ConnectionType.SLOW);
    }
  }

  private estimateConnectionSpeed(): void {
    // Test simple de la vitesse en téléchargeant un petit fichier et en mesurant le temps
    const startTime = Date.now();
    const img = new Image();

    img.onload = () => {
      const endTime = Date.now();
      const duration = endTime - startTime;

      if (duration > 300) {
        this.connectionTypeSubject.next(ConnectionType.SLOW);
      } else if (duration > 100) {
        this.connectionTypeSubject.next(ConnectionType.MEDIUM);
      } else {
        this.connectionTypeSubject.next(ConnectionType.FAST);
      }
    };

    img.onerror = () => {
      // En cas d'erreur, supposons une connexion lente
      this.connectionTypeSubject.next(ConnectionType.SLOW);
    };

    // Utiliser une image de 1x1 pixel transparent pour le test
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  }

  // Méthode pour récupérer le type de connexion actuel
  public getConnectionType(): Observable<ConnectionType> {
    return this.connectionTypeSubject.asObservable();
  }

  // Vérifier si la connexion est lente
  public isSlowConnection(): boolean {
    return this.connectionTypeSubject.value === ConnectionType.SLOW;
  }

  // Vérifier si la connexion est bonne (moyenne ou rapide)
  public hasGoodConnection(): boolean {
    return this.connectionTypeSubject.value !== ConnectionType.SLOW;
  }
}
